import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, Clock, CheckCircle, AlertCircle, RefreshCw, Eye, Copy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSubmissionsPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, read, replied
  const token = localStorage.getItem('token');

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/contact-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch contact submissions');
      if (error.response?.status === 403) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSubmissions();
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updateStatus = async (contactId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/contact-submissions/${contactId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'read': return 'bg-yellow-100 text-yellow-700';
      case 'replied': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <AlertCircle className="w-4 h-4" />;
      case 'read': return <Eye className="w-4 h-4" />;
      case 'replied': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === filter);

  const newCount = submissions.filter(s => s.status === 'new').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Admin
        </button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center">
              <MessageSquare className="w-8 h-8 mr-3 text-cyan-500" />
              Contact Submissions
              {newCount > 0 && (
                <span className="ml-3 px-3 py-1 bg-blue-500 text-white text-sm rounded-full font-semibold">
                  {newCount} new
                </span>
              )}
            </h1>
            <p className="text-slate-600 mt-2">View and manage messages from users</p>
          </div>
          <button
            onClick={fetchSubmissions}
            className="flex items-center px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'new', 'read', 'replied'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              data-testid={`filter-${status}`}
              className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                filter === status
                  ? 'bg-cyan-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' && (
                <span className="ml-2 text-sm opacity-75">
                  ({submissions.filter(s => status === 'all' || s.status === status).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border-2 border-dashed border-slate-200">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">
              {filter === 'all' ? 'No contact submissions yet' : `No ${filter} submissions`}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredSubmissions.map((submission) => (
              <div
                key={submission.contact_id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
                  submission.status === 'new' ? 'border-blue-200' : 'border-slate-100'
                }`}
              >
                <div 
                  className="p-5 cursor-pointer"
                  onClick={() => setSelectedSubmission(
                    selectedSubmission?.contact_id === submission.contact_id ? null : submission
                  )}
                  data-testid={`submission-${submission.contact_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(submission.status)}`}>
                          {getStatusIcon(submission.status)}
                          {submission.status}
                        </span>
                        <span className="text-slate-500 text-sm flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(submission.created_at)}
                        </span>
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1 truncate">{submission.subject}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center">
                          <span className="font-semibold mr-1">From:</span>
                          {submission.name}
                        </span>
                        <span className="flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {submission.email}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(submission.email);
                          toast.success('Email copied to clipboard!');
                        }}
                        className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1"
                        data-testid={`copy-email-${submission.contact_id}`}
                      >
                        <Copy className="w-3 h-3" />
                        Copy Email
                      </button>
                      <a
                        href={`mailto:${submission.email}?subject=Re: ${encodeURIComponent(submission.subject)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="px-3 py-1 bg-cyan-100 hover:bg-cyan-200 text-cyan-700 rounded-lg text-sm font-semibold transition-colors"
                        data-testid={`reply-${submission.contact_id}`}
                      >
                        Reply
                      </a>
                    </div>
                  </div>
                </div>

                {/* Expanded view */}
                {selectedSubmission?.contact_id === submission.contact_id && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50">
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-600 mb-2">Message:</h4>
                      <p className="text-slate-800 whitespace-pre-wrap bg-white p-4 rounded-lg border border-slate-200">
                        {submission.message}
                      </p>
                    </div>
                    
                    {submission.tour_id && (
                      <p className="text-sm text-slate-500 mb-4">
                        <span className="font-semibold">Related Tour ID:</span> {submission.tour_id}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-600 mr-2">Update Status:</span>
                      {['new', 'read', 'replied'].map((status) => (
                        <button
                          key={status}
                          onClick={() => updateStatus(submission.contact_id, status)}
                          data-testid={`status-${status}-${submission.contact_id}`}
                          className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                            submission.status === status
                              ? getStatusColor(status)
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          }`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSubmissionsPage;
